import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Tabs, formatPrice } from '@artosera/ui';
import {
  locationsApi,
  useEcheancesLocations,
  useReglagesLocations,
  useMutationLocation,
  type EcheanceLocation,
} from '../../api/locations.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import styles from './Locations.module.css';

export function EcheancesLocationsPage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const liste = useEcheancesLocations(galerieSlug);
  const reglages = useReglagesLocations(galerieSlug);
  const peutFacturer = galerieALaction(galerie, 'locations:facturer');
  const [selection, setSelection] = useState<EcheanceLocation | null>(null);
  const [automatique, setAutomatique] = useState<boolean | null>(null);
  const [erreur, setErreur] = useState('');
  const facturer = useMutationLocation(galerieSlug, (e: EcheanceLocation, appel) =>
    locationsApi.geste(
      galerieSlug,
      e.contratId,
      'echeances',
      { debutPeriode: e.debutPeriode },
      appel,
    ),
  );
  const regler = useMutationLocation(galerieSlug, (v: boolean, appel) =>
    locationsApi.regler(galerieSlug, v, appel),
  );
  const echec = (e: unknown) =>
    setErreur(e instanceof Error ? e.message : t('commun:erreurReseau'));
  return (
    <section className={styles.page}>
      <h1>{t('locations.echeances')}</h1>
      {(liste.isPending || reglages.isPending) && <p role="status">{t('commun:chargement')}</p>}
      {(liste.isError || reglages.isError) && <p role="alert">{t('commun:erreurReseau')}</p>}
      {erreur && <p role="alert">{erreur}</p>}
      {reglages.data && (
        <label>
          <input
            type="checkbox"
            disabled={!peutFacturer || regler.isPending}
            checked={reglages.data.reglages.emissionAutomatique}
            onChange={(e) => setAutomatique(e.target.checked)}
          />
          {t('locations.emissionAutomatique')}
        </label>
      )}
      <Tabs
        tabs={(['a_facturer', 'emises', 'impayees', 'a_rattraper'] as const).map((onglet) => ({
          value: onglet,
          label: t(`locations.onglets.${onglet}`),
          content: (
            <div className={styles.liste}>
              {liste.data?.[onglet].length === 0 && <p>{t('locations.aucuneEcheance')}</p>}
              {liste.data?.[onglet].map((e) => (
                <article className={styles.ligne} key={`${e.contratId}:${e.debutPeriode}`}>
                  <Link to={`/${galerieSlug}/locations/${e.contratId}`}>
                    {e.numero ?? t('locations.contrat')}
                  </Link>
                  <span>
                    {e.debutPeriode} — {e.finPeriode}
                  </span>
                  <span>
                    {formatPrice(e.montantHtCentimes, i18n.language === 'en' ? 'en' : 'fr', 'EUR')}
                  </span>
                  {e.facture ? (
                    <Link to={`/${galerieSlug}/ventes/${e.facture.id}`}>
                      {e.facture.numero ?? t('locations.ouvrirFacture')}
                    </Link>
                  ) : (
                    peutFacturer &&
                    ['a_facturer', 'a_rattraper'].includes(onglet) && (
                      <Button onClick={() => setSelection(e)}>
                        {t('locations.facturerPeriode')}
                      </Button>
                    )
                  )}
                </article>
              ))}
            </div>
          ),
        }))}
      />
      {selection && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setSelection(null);
          }}
          title={t('locations.facturerPeriode')}
          closeLabel={t('commun:actions.fermer')}
        >
          <p>
            {selection.debutPeriode} — {selection.finPeriode} ·{' '}
            {formatPrice(selection.montantHtCentimes, i18n.language === 'en' ? 'en' : 'fr', 'EUR')}
          </p>
          <Button
            disabled={facturer.isPending}
            onClick={() => {
              setErreur('');
              void facturer
                .mutateAsync(selection)
                .then(() => setSelection(null))
                .catch(echec);
            }}
          >
            {t('locations.confirmer')}
          </Button>
        </Dialog>
      )}
      {automatique !== null && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setAutomatique(null);
          }}
          title={t('locations.emissionAutomatique')}
          closeLabel={t('commun:actions.fermer')}
        >
          <p>{t(automatique ? 'locations.confirmerAutomatique' : 'locations.confirmerManuelle')}</p>
          <Button
            disabled={regler.isPending}
            onClick={() => {
              setErreur('');
              void regler
                .mutateAsync(automatique)
                .then(() => setAutomatique(null))
                .catch(echec);
            }}
          >
            {t('locations.confirmer')}
          </Button>
        </Dialog>
      )}
    </section>
  );
}
